import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LoadingCard } from "../components/LoadingSpinner";
import { formatDatePST } from "../utils/dateUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  MapPin,
  Users,
  Calendar,
  DollarSign,
  Filter,
  Search,
  Eye,
  Edit,
  Mail,
  Trash2,
  FolderOpen,
  Camera,
  ExternalLink,
  Link,
  Check,
  ArrowUpDown,
  AlertTriangle,
  Phone,
} from "lucide-react";
import { projectService } from "../services/projectService";
import { customerService } from "../services/customerService";
import { subcontractorService } from "../services/subcontractorService";
import { designerService } from "../services/designerService";
import { emailService } from "../services/emailService";
import { emailTemplateService } from "../services/emailTemplateService";
import { companyCamService } from "../services/companyCamService";
import { projectStatusService } from "../services/projectStatusService";
import { vendorService } from "../services/vendorService";
import QuickAddVendorDialog from "../components/projects/QuickAddVendorDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";

// Component Imports
import AddProjectDialog from "../components/projects/AddProjectDialog";
import EditProjectDialog from "../components/projects/EditProjectDialog";
import ProjectDetailDialog from "../components/projects/ProjectDetailDialog";

export default function Projects() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Check if user has edit permissions (not a basic 'user' role)
  const canEdit = user?.role !== 'user';
  const canDelete = user?.role === 'admin';
  
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProjectType, setFilterProjectType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [loadingProjectDetails, setLoadingProjectDetails] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  // Construction project management states
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [isChangeOrderDialogOpen, setIsChangeOrderDialogOpen] = useState(false);
  const [editingChangeOrderIndex, setEditingChangeOrderIndex] = useState(null);
  const [newChangeOrder, setNewChangeOrder] = useState({
    date: "",
    scope_of_work: "",
    amount: ""
  });
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: "",
    cost: "",
    company_name: "",
    description: "",
    date: "",
    vendor_id: "",
    invoice_number: ""
  });
  const [editMaterial, setEditMaterial] = useState(null);
  const [editMaterialIndex, setEditMaterialIndex] = useState(null);
  const [isEditMaterialDialogOpen, setIsEditMaterialDialogOpen] = useState(false);
  
  const [isReceiptDialogOpen, setIsReceiptDialogOpen] = useState(false);
  const [newReceipt, setNewReceipt] = useState({
    item: "",
    cost: "",
    company_name: "",
    description: "",
    date: "",
    vendor_id: "",
    invoice_number: ""
  });
  const [editReceipt, setEditReceipt] = useState(null);
  const [editReceiptIndex, setEditReceiptIndex] = useState(null);
  const [isEditReceiptDialogOpen, setIsEditReceiptDialogOpen] = useState(false);

  // Quick-add vendor/sub dialog state. `quickAddTarget` tells us which form to
  // write the new selection back into: 'newMaterial' | 'newReceipt' | 'editMaterial' | 'editReceipt'
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddTarget, setQuickAddTarget] = useState(null);

  // Status change dialog state
  const [isStatusChangeDialogOpen, setIsStatusChangeDialogOpen] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    projectId: null,
    projectName: "",
    newStatus: "",
    statusDate: new Date().toISOString().split('T')[0]
  });
  
  const [newProject, setNewProject] = useState({
    name: "",
    customer_id: "",
    company: "",
    project_type: "construction",
    jobsite_address: "",
    city: "",
    zip: "",
    priority: "medium",
    contract_sign_date: "",
    start_date: "",
    end_date: "",
    contract_price: "",
    crew_payroll_estimate: "",
    estimated_construction_budget: "",
    description: "",
    services: [],
    subcontractor_ids: [],
    designer_ids: [],
    assigned_user_ids: []
  });

  // Send Email states
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    template_id: "",
    to_email: "",
    to_name: "",
    subject: "",
    body: "",
    variables: {}
  });

  // CompanyCam states
  const [companyCamMapping, setCompanyCamMapping] = useState(null);
  const [isCompanyCamLinkDialogOpen, setIsCompanyCamLinkDialogOpen] = useState(false);
  const [isCompanyCamUnlinkDialogOpen, setIsCompanyCamUnlinkDialogOpen] = useState(false);
  const [companyCamSearchResults, setCompanyCamSearchResults] = useState([]);
  const [selectedCompanyCamProject, setSelectedCompanyCamProject] = useState(null);
  const [linkingType, setLinkingType] = useState("create"); // "create" or "link"
  const [searchingCompanyCam, setSearchingCompanyCam] = useState(false);
  const [allCompanyCamProjects, setAllCompanyCamProjects] = useState([]);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [companyCamSearchQuery, setCompanyCamSearchQuery] = useState("");
  const [companyCamCurrentPage, setCompanyCamCurrentPage] = useState(1); // Track current page
  const [companyCamHasMore, setCompanyCamHasMore] = useState(false); // Track if there are more pages
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [emailCustomer, setEmailCustomer] = useState(null);
  
  // Delete confirmation dialogs
  const [isDeleteChangeOrderDialogOpen, setIsDeleteChangeOrderDialogOpen] = useState(false);
  const [deletingChangeOrderId, setDeletingChangeOrderId] = useState(null);
  const [isDeleteMaterialDialogOpen, setIsDeleteMaterialDialogOpen] = useState(false);
  const [deletingMaterialIndex, setDeletingMaterialIndex] = useState(null);
  const [isDeleteReceiptDialogOpen, setIsDeleteReceiptDialogOpen] = useState(false);
  const [deletingReceiptIndex, setDeletingReceiptIndex] = useState(null);
  
  // Queries
  const { data: projectsData, isLoading: loading } = useQuery({
    queryKey: ['projects', filterStatus, filterProjectType, searchTerm, sortBy],
    queryFn: () => projectService.getAll({ 
      status: filterStatus === "all" ? undefined : filterStatus,
      project_type: filterProjectType === "all" ? undefined : filterProjectType,
      search: searchTerm || undefined,
      sort_by: sortBy,
      limit: 500  // Fetch all projects, pagination is handled client-side
    })
  });
  
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getAll()
  });
  
  const { data: subcontractorsData } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: () => subcontractorService.getAll()
  });
  
  const { data: designersData } = useQuery({
    queryKey: ['designers'],
    queryFn: () => designerService.getAll()
  });
  
  const { data: vendorsData } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getAll()
  });

  // Mutations - will be defined below
  const createProjectMutation = useMutation({
    mutationFn: (data) => projectService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Project created successfully!" });
      setNewProject({
        name: "",
        customer_id: "",
        project_type: "construction",
        jobsite_address: "",
        city: "",
        zip: "",
        priority: "medium",
        start_date: "",
        end_date: "",
        contract_price: "",
        crew_payroll_estimate: "",
        estimated_construction_budget: "",
        description: "",
        services: [],
        subcontractor_ids: [],
        designer_ids: [],
        assigned_user_ids: []
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to create project" });
    }
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ id, data }) => projectService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Project updated successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to update project" });
    }
  });

  const addMaterialMutation = useMutation({
    mutationFn: ({ id, material }) => projectService.addMaterial(id, material),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Material added successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to add material" });
    }
  });

  const updateMaterialMutation = useMutation({
    mutationFn: ({ id, index, material }) => projectService.updateMaterial(id, index, material),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Material updated successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to update material" });
    }
  });

  const deleteMaterialMutation = useMutation({
    mutationFn: ({ id, index }) => projectService.deleteMaterial(id, index),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Material deleted successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to delete material" });
    }
  });

  const addReceiptMutation = useMutation({
    mutationFn: ({ id, receipt }) => projectService.addReceipt(id, receipt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Receipt added successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to add receipt" });
    }
  });

  const updateReceiptMutation = useMutation({
    mutationFn: ({ id, index, receipt }) => projectService.updateReceipt(id, index, receipt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Receipt updated successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to update receipt" });
    }
  });

  const deleteReceiptMutation = useMutation({
    mutationFn: ({ id, index }) => projectService.deleteReceipt(id, index),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Receipt deleted successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to delete receipt" });
    }
  });

  const addChangeOrderMutation = useMutation({
    mutationFn: ({ id, changeOrder }) => projectService.addChangeOrder(id, changeOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Change order added successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to add change order" });
    }
  });

  const updatePaymentMutation = useMutation({
    mutationFn: ({ id, paymentNumber, paymentData }) => projectService.updatePayment(id, paymentNumber, paymentData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Payment updated successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to update payment" });
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => emailService.send(data),
    onSuccess: (response) => {
      const warnings = response?.data?.warnings || response?.warnings || [];
      if (warnings.length > 0) {
        toast({ description: `Email sent! Warning: ${warnings.join('; ')}`, duration: 8000 });
      } else {
        toast({ description: "Email sent successfully!" });
      }
      setIsSendEmailDialogOpen(false);
      setEmailData({ template_id: "", to_email: "", to_name: "", subject: "", body: "", variables: {} });
      setSelectedEmailTemplate(null);
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to send email" });
    }
  });

  const location = useLocation();

  // Handle opening project from search (via query parameter)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('openId');
    
    if (openId) {
      // Directly fetch the project by ID and open dialog
      const fetchAndOpen = async () => {
        try {
          console.log(`Attempting to fetch project details for ID: ${openId}`);
          const response = await projectService.getById(openId);
          console.log('Fetched project details:', response.data);
          setSelectedProject(response.data);
          setIsDetailDialogOpen(true);
          // Clear the query parameter from URL
          window.history.replaceState({}, '', '/projects');
        } catch (error) {
          console.error("Failed to fetch project details:", error);
          toast({
            variant: "destructive",
            description: "Failed to load project details"
          });
          // Clear the query parameter from URL
          window.history.replaceState({}, '', '/projects');
        }
      };
      fetchAndOpen();
    }
  }, [location.search]); // Only depend on location.search

  // Check if we should auto-open a project (from customer create project)
  useEffect(() => {
    const openProjectId = sessionStorage.getItem('openProjectId');
    if (openProjectId && projectsData?.data) {
      // Find the project in the loaded projects
      const projectToOpen = projectsData.data.find(p => (p.id || p._id) === openProjectId);
      if (projectToOpen) {
        // Open the project detail dialog
        setSelectedProject(projectToOpen);
        setIsDetailDialogOpen(true);
        // Clear the flag
        sessionStorage.removeItem('openProjectId');
      }
    }
  }, [projectsData]);


  
  // State for tracking initialization
  const [statusesInitialized, setStatusesInitialized] = React.useState(false);
  
  // Initialize project statuses first
  React.useEffect(() => {
    const initializeStatuses = async () => {
      try {
        await projectStatusService.initialize();
        setStatusesInitialized(true);
      } catch (error) {
        // Already initialized or other error, continue anyway
        setStatusesInitialized(true);
      }
    };
    initializeStatuses();
  }, []);
  
  // Direct API call state
  const [projectStatusesData, setProjectStatusesData] = React.useState(null);
  const [statusesLoading, setStatusesLoading] = React.useState(true);
  
  // Fetch statuses only after initialization with direct API call
  React.useEffect(() => {
    const fetchStatuses = async () => {
      if (!statusesInitialized) return;
      
      try {
        setStatusesLoading(true);
        const response = await projectStatusService.getAll();
        setProjectStatusesData(response);
      } catch (error) {
        console.error('Error fetching project statuses:', error);
      } finally {
        setStatusesLoading(false);
      }
    };
    
    fetchStatuses();
  }, [statusesInitialized]);
  
  // Fetch CompanyCam projects when dialog opens or search query changes
  useEffect(() => {
    const fetchCompanyCamProjects = async () => {
      if (isCompanyCamLinkDialogOpen && linkingType === 'link') {
        setSearchingCompanyCam(true);
        setAllCompanyCamProjects([]); // Clear existing projects
        setCompanyCamCurrentPage(1);
        
        try {
          const response = await companyCamService.getAllProjects(1, 25);
          // Response is wrapped in ResponseFormatter.success()
          // Structure: { success: true, data: { projects: [...], page: ..., per_page: ..., has_more: ... }, message: "..." }
          const projects = response?.data?.projects || [];
          const hasMore = response?.data?.has_more || false;
          
          setAllCompanyCamProjects(projects);
          setCompanyCamHasMore(hasMore);
          
          console.log(`Loaded ${projects.length} CompanyCam projects (page 1, has_more: ${hasMore})`);
        } catch (error) {
          console.error("Failed to fetch CompanyCam projects:", error);
        } finally {
          setSearchingCompanyCam(false);
        }
      }
    };

    fetchCompanyCamProjects();
    
    // Reset selection and pagination when dialog closes or type changes
    if (!isCompanyCamLinkDialogOpen || linkingType !== 'link') {
      setSelectedCompanyCamProject(null);
      setCompanyCamSearchQuery("");
      setAllCompanyCamProjects([]);
      setCompanyCamCurrentPage(1);
      setCompanyCamHasMore(false);
    }
  }, [isCompanyCamLinkDialogOpen, linkingType]);
  
  // Extract project statuses with fallback
  const projectStatuses = React.useMemo(() => {
    if (projectStatusesData?.data && Array.isArray(projectStatusesData.data)) {
      return projectStatusesData.data.map(status => ({
        value: status.value,
        label: status.label,
        color: status.color,
        isActive: status.is_active
      }));
    }
    
    // Fallback to basic statuses if API is loading or fails
    return [
      { value: "design_creation", label: "Design Creation", color: "bg-blue-500", isActive: true },
      { value: "construction_phase", label: "Construction Phase", color: "bg-yellow-500", isActive: true }
    ];
  }, [projectStatusesData]);
  
  // CompanyCam mutations
  const linkCompanyCamMutation = useMutation({
    mutationFn: (data) => companyCamService.linkProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "CompanyCam project linked successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to link CompanyCam project" });
    }
  });

  const unlinkCompanyCamMutation = useMutation({
    mutationFn: (projectId) => companyCamService.unlinkProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "CompanyCam project unlinked successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to unlink CompanyCam project" });
    }
  });

  // Fetch email templates for sending (only project templates)
  const { data: templatesData } = useQuery({
    queryKey: ['email-templates', 'project'],
    queryFn: () => emailTemplateService.getAll({ 
      active_only: true,
      category: 'project'
    })
  });

  const projects = projectsData?.data || [];
  const customers = customersData?.data || [];
  const subcontractors = subcontractorsData?.data || [];
  const designers = designersData?.data || [];
  const vendors = vendorsData?.data || [];

  // Open the quick-add dialog, remembering which form to write the result back to
  const openQuickAdd = (target) => {
    setQuickAddTarget(target);
    setQuickAddOpen(true);
  };

  // When a vendor/sub is created, refresh the lists and select it in the right form
  const handleQuickAddCreated = async ({ id, name, type }) => {
    // Refresh whichever list got a new entry so the dropdown shows it
    if (type === "vendor") {
      await queryClient.invalidateQueries({ queryKey: ['vendors'] });
    } else {
      await queryClient.invalidateQueries({ queryKey: ['subcontractors'] });
    }
    const patch = { vendor_id: id, company_name: name };
    if (quickAddTarget === "newMaterial") setNewMaterial(prev => ({ ...prev, ...patch }));
    else if (quickAddTarget === "newReceipt") setNewReceipt(prev => ({ ...prev, ...patch }));
    else if (quickAddTarget === "editMaterial") setEditMaterial(prev => ({ ...prev, ...patch }));
    else if (quickAddTarget === "editReceipt") setEditReceipt(prev => ({ ...prev, ...patch }));
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Calculate pagination
  const totalPages = Math.ceil(projects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjects = projects.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterProjectType, searchTerm]);

  // Auto-open project detail if navigated from customer creation
  useEffect(() => {
    const newProjectId = sessionStorage.getItem('newProjectId');
    if (newProjectId && projectsData?.data) {
      // Find the newly created project
      const newProject = projectsData.data.find(p => (p.id || p._id) === newProjectId);
      if (newProject) {
        // Open the detail dialog
        setSelectedProject(newProject);
        setIsDetailDialogOpen(true);
        // Clear the sessionStorage
        sessionStorage.removeItem('newProjectId');
      }
    }
  }, [projectsData]);

  const getStatusColor = (status) => {
    const statusObj = projectStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : "bg-gray-500";
  };

  const getStatusLabel = (status) => {
    const statusObj = projectStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const handleAddProject = async () => {
    try {
      const projectData = {
        ...newProject,
        contract_price: parseFloat(newProject.contract_price) || 0,
        crew_payroll_estimate: parseFloat(newProject.crew_payroll_estimate) || 0,
        estimated_construction_budget: newProject.project_type === "design_only" ? parseFloat(newProject.estimated_construction_budget) || 0 : undefined,
        services: newProject.services.filter(Boolean)
      };
      
      createProjectMutation.mutate(projectData);
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  // Open status change dialog instead of directly changing
  const handleStatusChangeClick = (project, newStatus) => {
    setStatusChangeData({
      projectId: project.id || project._id,
      projectName: project.name,
      newStatus: newStatus,
      statusDate: new Date().toISOString().split('T')[0]
    });
    setIsStatusChangeDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    try {
      await projectService.updateStatus(
        statusChangeData.projectId, 
        statusChangeData.newStatus, 
        null, 
        null,
        statusChangeData.statusDate
      );
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setIsStatusChangeDialogOpen(false);
      toast({ description: "Project status updated successfully!" });
    } catch (error) {
      console.error("Failed to update project status:", error);
      toast({ 
        variant: "destructive",
        description: "Failed to update project status"
      });
    }
  };

  const [isDeleteProjectDialogOpen, setIsDeleteProjectDialogOpen] = React.useState(false);
  const [projectToDelete, setProjectToDelete] = React.useState(null);

  const handleDeleteProject = async (project) => {
    // Open confirmation dialog
    setProjectToDelete(project);
    setIsDeleteProjectDialogOpen(true);
  };
  
  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    
    const projectId = projectToDelete.id || projectToDelete._id;
    
    try {
      console.log(`🗑️ Deleting project: ${projectToDelete.name}`);
      await projectService.delete(projectId);
      console.log("✅ Project deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ description: "Project deleted successfully!" });
    } catch (error) {
      console.error("❌ Failed to delete project:", error);
      toast({ 
        variant: "destructive",
        description: "Failed to delete project" 
      });
    } finally {
      setIsDeleteProjectDialogOpen(false);
      setProjectToDelete(null);
    }
  };
  
  // --- Project Handler Functions (Extracted Logic) ---

  const refreshSelectedProject = async () => {
    try {
      const response = await projectService.getById(selectedProject.id || selectedProject._id);
      setSelectedProject(response.data);
      
      // Also fetch CompanyCam mapping if project is open
      try {
        const mappingResponse = await companyCamService.getProjectMapping(selectedProject.id || selectedProject._id);
        if (mappingResponse.success && mappingResponse.data) {
          setCompanyCamMapping(mappingResponse.data);
        } else {
          setCompanyCamMapping(null);
        }
      } catch (error) {
        // Project not linked to CompanyCam
        setCompanyCamMapping(null);
      }
    } catch (error) {
      console.error("Failed to refresh selected project:", error);
      // Don't throw - just log the error
    }
  };

  const handleEditProject = (project) => {
    setEditingProject({
      ...project,
      project_type: project.project_type || "construction",
      contract_price: project.contract_price || project.budget || 0,
      crew_payroll_estimate: project.crew_payroll_estimate || 0,
      jobsite_address: project.jobsite_address || project.address || "",
      estimated_construction_budget: project.estimated_construction_budget || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      subcontractor_ids: project.subcontractor_ids || [],
      designer_ids: project.designer_ids || []
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateProject = () => {
    const projectData = {
      name: editingProject.name,
      customer_id: editingProject.customer_id,
      project_type: editingProject.project_type,
      jobsite_address: editingProject.jobsite_address,
      city: editingProject.city,
      zip: editingProject.zip,
      priority: editingProject.priority,
      start_date: editingProject.start_date,
      end_date: editingProject.end_date,
      contract_sign_date: editingProject.contract_sign_date,
      contract_price: parseFloat(editingProject.contract_price || "0") || 0,
      crew_payroll_estimate: parseFloat(editingProject.crew_payroll_estimate || "0") || 0,
      estimated_construction_budget: editingProject.project_type === "design_only" ? parseFloat(editingProject.estimated_construction_budget || "0") || 0 : undefined,
      description: editingProject.description,
      services: editingProject.services ? editingProject.services.filter(Boolean) : [],
      subcontractor_ids: editingProject.subcontractor_ids || [],
      designer_ids: editingProject.designer_ids || []
    };
    
    updateProjectMutation.mutate({
      id: editingProject.id || editingProject._id,
      data: projectData
    }, {
      onSuccess: () => {
        setEditingProject(null);
        setIsEditDialogOpen(false);
      }
    });
  };

  const handleAddOrUpdateChangeOrder = () => {
    if (editingChangeOrderIndex !== null) {
      // Update existing change order
      const updatedChangeOrders = selectedProject.change_orders.map((co, idx) => 
        idx === editingChangeOrderIndex 
          ? { 
              ...co, // Preserve existing fields like added_at
              date: newChangeOrder.date,
              scope_of_work: newChangeOrder.scope_of_work,
              amount: parseFloat(newChangeOrder.amount) || 0 
            }
          : co
      );
      
      updateProjectMutation.mutate({
        id: selectedProject.id || selectedProject._id,
        data: { change_orders: updatedChangeOrders }
      }, {
        onSuccess: () => {
          setNewChangeOrder({ date: "", scope_of_work: "", amount: "" });
          setEditingChangeOrderIndex(null);
          setIsChangeOrderDialogOpen(false);
          refreshSelectedProject();
        }
      });
    } else {
      // Add new change order
      addChangeOrderMutation.mutate({
        id: selectedProject.id || selectedProject._id,
        changeOrder: {
          ...newChangeOrder,
          amount: parseFloat(newChangeOrder.amount) || 0
        }
      }, {
        onSuccess: () => {
          setNewChangeOrder({ date: "", scope_of_work: "", amount: "" });
          setIsChangeOrderDialogOpen(false);
          refreshSelectedProject();
        }
      });
    }
  };

  const handleEditChangeOrder = (changeOrder, index) => {
    setNewChangeOrder({
      date: changeOrder.date,
      scope_of_work: changeOrder.scope_of_work,
      amount: changeOrder.amount.toString()
    });
    setEditingChangeOrderIndex(index);
    setIsChangeOrderDialogOpen(true);
  };

  const handleDeleteChangeOrder = (index) => {
    setDeletingChangeOrderId(index);
    setIsDeleteChangeOrderDialogOpen(true);
  };

  const confirmDeleteChangeOrder = () => {
    if (deletingChangeOrderId === null) return;
    
    const updatedChangeOrders = selectedProject.change_orders.filter((_, idx) => idx !== deletingChangeOrderId);
    
    updateProjectMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      data: { change_orders: updatedChangeOrders }
    }, {
      onSuccess: () => {
        refreshSelectedProject();
        setIsDeleteChangeOrderDialogOpen(false);
        setDeletingChangeOrderId(null);
      }
    });
  };

  const handleUpdatePayment = () => {
    const paymentData = {
      due_date: editingPayment.due_date,
      paid_date: editingPayment.paid_date,
      status: editingPayment.status,
      description: (editingPayment.description || "").trim() || undefined,
      payment_method: editingPayment.payment_method || "",
      payment_reference: editingPayment.payment_reference || "",
    };

    // Include amount if it's not Final Payment OR if Final Payment has override enabled
    const isFinalPayment = editingPayment.payment_number === (selectedProject?.payment_schedule?.length || 4);
    if (!isFinalPayment || editingPayment.override) {
      paymentData.amount = editingPayment.amount;
    }

    // Include override flag if present
    if (editingPayment.override !== undefined) {
      paymentData.override = editingPayment.override;
    }
    
    updatePaymentMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      paymentNumber: editingPayment.payment_number,
      paymentData
    }, {
      onSuccess: () => {
        setEditingPayment(null);
        setIsPaymentDialogOpen(false);
        refreshSelectedProject();
      }
    });
  };

  const handleAddMaterial = () => {
    addMaterialMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      material: {
        ...newMaterial,
        cost: parseFloat(newMaterial.cost) || 0,
        vendor_id: newMaterial.vendor_id || undefined
      }
    }, {
      onSuccess: () => {
        setNewMaterial({ name: "", cost: "", company_name: "", description: "", date: "", vendor_id: "", invoice_number: "" });
        setIsMaterialDialogOpen(false);
        refreshSelectedProject();
      }
    });
  };

  const handleAddReceipt = () => {
    addReceiptMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      receipt: {
        ...newReceipt,
        cost: parseFloat(newReceipt.cost) || 0,
        vendor_id: newReceipt.vendor_id || undefined
      }
    }, {
      onSuccess: () => {
        setNewReceipt({ item: "", cost: "", company_name: "", description: "", date: "", vendor_id: "", invoice_number: "" });
        setIsReceiptDialogOpen(false);
        refreshSelectedProject();
      }
    });
  };

  const handleEditMaterial = (material, index) => {
    setEditMaterial({ ...material });
    setEditMaterialIndex(index);
    setIsEditMaterialDialogOpen(true);
  };

  const handleUpdateMaterial = () => {
    updateMaterialMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      index: editMaterialIndex,
      material: {
        ...editMaterial,
        cost: parseFloat(editMaterial.cost) || 0,
        vendor_id: editMaterial.vendor_id || undefined
      }
    }, {
      onSuccess: () => {
        setEditMaterial(null);
        setEditMaterialIndex(null);
        setIsEditMaterialDialogOpen(false);
        refreshSelectedProject();
      }
    });
  };

  const handleDeleteMaterial = (materialIndex) => {
    setDeletingMaterialIndex(materialIndex);
    setIsDeleteMaterialDialogOpen(true);
  };

  const confirmDeleteMaterial = () => {
    if (deletingMaterialIndex === null) return;
    
    deleteMaterialMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      index: deletingMaterialIndex
    }, {
      onSuccess: () => {
        refreshSelectedProject();
        setIsDeleteMaterialDialogOpen(false);
        setDeletingMaterialIndex(null);
      }
    });
  };

  const handleEditReceipt = (receipt, index) => {
    setEditReceipt({ ...receipt });
    setEditReceiptIndex(index);
    setIsEditReceiptDialogOpen(true);
  };

  const handleUpdateReceipt = () => {
    updateReceiptMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      index: editReceiptIndex,
      receipt: {
        ...editReceipt,
        cost: parseFloat(editReceipt.cost) || 0,
        vendor_id: editReceipt.vendor_id || undefined
      }
    }, {
      onSuccess: () => {
        setEditReceipt(null);
        setEditReceiptIndex(null);
        setIsEditReceiptDialogOpen(false);
        refreshSelectedProject();
      }
    });
  };

  const handleDeleteReceipt = (receiptIndex) => {
    setDeletingReceiptIndex(receiptIndex);
    setIsDeleteReceiptDialogOpen(true);
  };

  const confirmDeleteReceipt = () => {
    if (deletingReceiptIndex === null) return;
    
    deleteReceiptMutation.mutate({
      id: selectedProject.id || selectedProject._id,
      index: deletingReceiptIndex
    }, {
      onSuccess: () => {
        refreshSelectedProject();
        setIsDeleteReceiptDialogOpen(false);
        setDeletingReceiptIndex(null);
      }
    });
  };

  const handleSendEmail = async (project) => {
    // Get customer details for the project
    let customer = null;
    if (project.customer_id) {
      try {
        const customerResponse = await customerService.getById(project.customer_id);
        customer = customerResponse.data;
      } catch (error) {
        console.error("Failed to fetch customer:", error);
      }
    }

    if (!customer || !customer.email) {
      alert("Customer email not found for this project.");
      return;
    }

    setEmailCustomer(customer);
    setEmailData({
      template_id: "",
      to_email: customer.email || "",
      to_name: customer.name || "",
      subject: "",
      body: "",
      variables: {}
    });
    setSelectedProject(project);
    setSelectedEmailTemplate(null);
    setIsSendEmailDialogOpen(true);
  };

  // Function to preview email content with variables replaced
  const getPreviewContent = (text) => {
    if (!text) return text;
    
    let result = text;
    const project = selectedProject || {};
    const customer = emailCustomer || {};
    
    // Format dates nicely
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (e) {
        return dateStr;
      }
    };
    
    // Format appointment_date if present
    let formattedAppointmentDate = '';
    if (customer.appointment_date) {
      try {
        const date = new Date(customer.appointment_date);
        formattedAppointmentDate = date.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        formattedAppointmentDate = customer.appointment_date;
      }
    }
    
    // Build a variables map from customer and project data
    const variables = {
      // Customer fields - basic
      name: customer.name || '',
      last_name: customer.last_name || '',
      full_name: `${customer.name || ''} ${customer.last_name || ''}`.trim(),
      email: customer.email || '',
      phone: customer.phone || '',
      spouse: customer.spouse || '',
      spouse_phone: customer.spouse_phone || '',
      spouse_email: customer.spouse_email || '',
      appointment_date: formattedAppointmentDate,
      // Customer fields with customer_ prefix
      customer_name: customer.name || '',
      customer_last_name: customer.last_name || '',
      customer_full_name: `${customer.name || ''} ${customer.last_name || ''}`.trim(),
      customer_email: customer.email || '',
      customer_phone: customer.phone || '',
      customer_address: customer.address || '',
      customer_city: customer.city || '',
      customer_zip_code: customer.zip_code || '',
      customer_spouse: customer.spouse || '',
      // Project fields
      project_name: project.name || '',
      status: project.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '',
      address: project.jobsite_address || project.address || customer.address || '',
      jobsite_address: project.jobsite_address || '',
      city: project.city || customer.city || '',
      zip: project.zip || '',
      zip_code: project.zip || customer.zip_code || '',
      start_date: formatDate(project.start_date),
      end_date: formatDate(project.end_date),
      contract_sign_date: formatDate(project.contract_sign_date),
      contract_price: project.contract_price ? `$${Number(project.contract_price).toLocaleString()}` : '',
    };

    // Payment schedule derived variables (mirror what backend computes at send time)
    const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDue = (d) => {
      if (!d) return '';
      try {
        const dt = new Date(d.length >= 10 ? d.substring(0, 10) + 'T00:00:00' : d);
        return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      } catch (e) { return d; }
    };
    const schedule = Array.isArray(project.payment_schedule) ? project.payment_schedule : [];
    const baseContract = parseFloat(project.contract_price || 0);
    const changeOrdersTotal = (project.change_orders || []).reduce((s, co) => s + (parseFloat(co?.amount) || 0), 0);
    const totalContract = baseContract + changeOrdersTotal;
    const totalPaid = schedule.filter(p => p?.status === 'paid' || p?.paid_date).reduce((s, p) => s + (parseFloat(p?.amount) || 0), 0);
    const remaining = Math.max(0, totalContract - totalPaid);

    const first = schedule[0];
    const last = schedule[schedule.length - 1];
    Object.assign(variables, {
      deposit_amount: first ? fmtMoney(first.amount) : '',
      deposit_due_date: first ? fmtDue(first.due_date) : '',
      deposit_description: first?.description || '',
      final_payment_amount: last ? fmtMoney(last.amount) : '',
      final_payment_due_date: last ? fmtDue(last.due_date) : '',
      total_contract: fmtMoney(totalContract),
      total_paid: fmtMoney(totalPaid),
      remaining_balance: fmtMoney(remaining),
    });
    
    // Replace variables
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{${key}}`;
      if (result.includes(placeholder)) {
        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || `[${key}]`);
      }
    }
    
    return result;
  };

  // Check for unresolved variables
  const getUnresolvedVariables = (text) => {
    if (!text) return [];
    const variablePattern = /\{([^}]+)\}/g;
    const matches = text.match(variablePattern) || [];
    return [...new Set(matches)];
  };

  const handleTemplateChange = async (templateId) => {
    try {
      // Fetch full template details
      const response = await emailTemplateService.getById(templateId);
      const template = response.data;
      
      setSelectedEmailTemplate(template);
      setEmailData({
        ...emailData,
        template_id: templateId,
        subject: template?.subject || "",
        body: template?.body || ""
      });
    } catch (error) {
      console.error("Failed to fetch template:", error);
      // Fallback to basic template from list
      const template = (templatesData?.data?.templates || []).find(t => (t.id || t._id) === templateId);
      setSelectedEmailTemplate(template);
      setEmailData({
        ...emailData,
        template_id: templateId,
        subject: template?.subject || "",
        body: template?.body || ""
      });
    }
  };

  const handleSendEmailSubmit = async () => {
    const sendPayload = {
      ...emailData,
      customer_id: emailCustomer?.id || emailCustomer?._id,
      project_id: selectedProject?.id || selectedProject?._id
    };

    // Preview first to check for missing variable data
    try {
      const preview = await emailService.preview(sendPayload);
      const previewData = preview?.data || preview;
      const warnings = previewData?.warnings || [];

      if (warnings.length > 0) {
        const warningMessages = warnings.map(w => w.message || w).join('\n\n');
        toast({
          variant: "destructive",
          title: "Cannot send — missing variable data",
          description: warningMessages,
          duration: 10000
        });
        return;
      }
    } catch (err) {
      console.error("Preview check failed:", err);
    }

    sendEmailMutation.mutate(sendPayload, {
      onSuccess: () => {
        setIsSendEmailDialogOpen(false);
        setEmailData({
          template_id: "",
          to_email: "",
          to_name: "",
          subject: "",
          body: "",
          variables: {}
        });
        setSelectedEmailTemplate(null);
        setEmailCustomer(null);
        if (isDetailDialogOpen && selectedProject) {
          refreshSelectedProject();
        }
      }
    });
  };

  const handleLoadMoreCompanyCamProjects = async () => {
    if (searchingCompanyCam || !companyCamHasMore) return;
    
    setSearchingCompanyCam(true);
    try {
      const nextPage = companyCamCurrentPage + 1;
      const response = await companyCamService.getAllProjects(nextPage, 25);
      
      const newProjects = response?.data?.projects || [];
      const hasMore = response?.data?.has_more || false;
      
      // Append new projects to existing ones
      setAllCompanyCamProjects(prev => [...prev, ...newProjects]);
      setCompanyCamCurrentPage(nextPage);
      setCompanyCamHasMore(hasMore);
      
      console.log(`Loaded ${newProjects.length} more CompanyCam projects (page ${nextPage}, has_more: ${hasMore})`);
    } catch (error) {
      console.error("Failed to load more CompanyCam projects:", error);
    } finally {
      setSearchingCompanyCam(false);
    }
  };

  const handleLinkToCompanyCam = (type, projectData = null) => {
    if (type === "create") {
      linkCompanyCamMutation.mutate({
        crm_project_id: selectedProject.id || selectedProject._id,
        name: selectedProject.name,
        address: selectedProject.jobsite_address || selectedProject.address,
        customer_name: selectedProject.customer?.name
      }, {
        onSuccess: (result) => {
          setCompanyCamMapping({
            linked: true,
            companycam_project: result.data || result
          });
          setIsCompanyCamLinkDialogOpen(false);
          setSelectedCompanyCamProject(null);
        }
      });
    } else if (type === "link" && selectedCompanyCamProject) {
      linkCompanyCamMutation.mutate({
        crm_project_id: selectedProject.id || selectedProject._id,
        companycam_project_id: selectedCompanyCamProject.id
      }, {
        onSuccess: (result) => {
          setCompanyCamMapping({
            linked: true,
            companycam_project: result.data || result
          });
          setIsCompanyCamLinkDialogOpen(false);
          setSelectedCompanyCamProject(null);
        }
      });
    }
  };

  const handleUnlinkCompanyCam = () => {
    setIsCompanyCamUnlinkDialogOpen(true);
  };

  const confirmUnlinkCompanyCam = () => {
    unlinkCompanyCamMutation.mutate(selectedProject.id || selectedProject._id, {
      onSuccess: () => {
        setCompanyCamMapping(null);
        setIsCompanyCamUnlinkDialogOpen(false);
      }
    });
  };

  // --- End Project Handler Functions ---
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your active landscaping projects</p>
        </div>
        <div className="w-full sm:w-auto">
          <AddProjectDialog
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            newProject={newProject}
            setNewProject={setNewProject}
            handleAddProject={handleAddProject}
            creating={createProjectMutation.isPending}
            customers={customers}
            designers={designers}
            subcontractors={subcontractors}
          />
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3">
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search projects by name or customer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full h-10"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-40 h-10">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {projectStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterProjectType} onValueChange={setFilterProjectType}>
                <SelectTrigger className="w-full sm:w-44 h-10">
                  <SelectValue placeholder="Project Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="design_only">Design Only</SelectItem>
                  <SelectItem value="construction">Construction</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-52 h-10">
                  <ArrowUpDown className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Most Recent</SelectItem>
                  <SelectItem value="updated_at">Recently Updated</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="customer_last_name">Customer Last Name</SelectItem>
                  <SelectItem value="contract_sign_date">Contract Sign Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <LoadingCard>Loading projects...</LoadingCard>
          <LoadingCard />
          <LoadingCard />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {paginatedProjects.map((project) => (
          <Card key={project.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3 sm:pb-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg truncate">{project.name}</CardTitle>
                  <CardDescription className="flex items-center mt-1">
                    <Users className="mr-1 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{project.customer}</span>
                  </CardDescription>
                  <div className="mt-1">
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        project.project_type === "design_only"
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-blue-100 text-blue-800 border-blue-200"
                      }`}
                    >
                      {project.project_type === "design_only" ? "Design Only" : "Construction"}
                    </Badge>
                  </div>
                </div>
                <Select
                  value={project.status}
                  onValueChange={(value) => handleStatusChangeClick(project, value)}
                >
                  <SelectTrigger className="w-auto h-8 sm:h-9">
                    <Badge className={`${getStatusColor(project.status)} text-white text-xs`}>
                      {getStatusLabel(project.status)}
                    </Badge>
                  </SelectTrigger>
                  <SelectContent>
                    {projectStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="truncate">
                    {(project.jobsite_address || project.address)
                      ? `${project.jobsite_address || project.address}${project.city ? `, ${project.city}` : ''}${project.zip ? ` ${project.zip}` : ''}`
                      : 'No address'}
                  </span>
                </div>

                {(() => {
                  const base = parseFloat(project.contract_price || project.budget || 0);
                  const coTotal = (project.change_orders || []).reduce((s, co) => s + (parseFloat(co?.amount) || 0), 0);
                  const totalJob = parseFloat(project.total_job_cost || 0) || (base + coTotal);
                  return (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center text-gray-600">
                          <Phone className="mr-1 h-4 w-4" />
                          {project.customer_phone || 'No phone'}
                        </span>
                        <span className="flex items-center text-green-600 font-medium">
                          <DollarSign className="mr-1 h-4 w-4" />
                          ${totalJob.toLocaleString()}
                        </span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="mr-1 h-4 w-4" />
                        Start Date: {project.start_date ? formatDatePST(project.start_date) : 'No date'}
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                          <span>Progress</span>
                          <span>{project.progress}%</span>
                        </div>
                        <Progress value={project.progress} className="h-2" />
                      </div>
                    </>
                  );
                })()}

                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Users className="h-3 w-3" />
                  <span className="truncate">Team: {project.team && project.team.length > 0 ? project.team.join(", ") : "Not assigned"}</span>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 min-w-0 h-10 sm:h-8"
                    onClick={async () => {
                      setLoadingProjectDetails(true);
                      try {
                        const response = await projectService.getById(project.id || project._id);
                        setSelectedProject(response.data);

                        // Fetch CompanyCam mapping
                        try {
                          const mappingResponse = await companyCamService.getProjectMapping(project.id || project._id);
                          if (mappingResponse.success && mappingResponse.data) {
                            setCompanyCamMapping(mappingResponse.data);
                          } else {
                            setCompanyCamMapping(null);
                          }
                        } catch (error) {
                          // Project not linked to CompanyCam
                          setCompanyCamMapping(null);
                        }
                      } catch (error) {
                        console.error("Failed to fetch project details:", error);
                        setSelectedProject(project); // Fallback to cached data
                        setCompanyCamMapping(null);
                      }
                      setLoadingProjectDetails(false);
                      setIsDetailDialogOpen(true);
                    }}
                  >
                    <Eye className="mr-1 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-10 sm:h-8 px-3"
                    onClick={() => handleSendEmail(project)}
                  >
                    <Mail className="mr-1 h-4 w-4 sm:mr-0.5 sm:h-3 sm:w-3" />
                    <span className="sm:text-xs">Email</span>
                  </Button>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-10 sm:h-8 px-3"
                      onClick={() => handleEditProject(project)}
                    >
                      <Edit className="mr-1 h-4 w-4 sm:mr-0.5 sm:h-3 sm:w-3" />
                      <span className="sm:text-xs">Edit</span>
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-10 sm:h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteProject(project)}
                      title="Delete project"
                    >
                      <Trash2 className="h-4 w-4 sm:h-3 sm:w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Pagination Controls */}
      {!loading && projects.length > 0 && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
          <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
            Showing {startIndex + 1}-{Math.min(endIndex, projects.length)} of {projects.length} projects
          </div>
          <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-center sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              className="h-10 sm:h-8"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                const showEllipsis =
                  (page === 2 && currentPage > 3) ||
                  (page === totalPages - 1 && currentPage < totalPages - 2);

                if (showEllipsis) {
                  return <span key={page} className="px-1 sm:px-2">...</span>;
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className={`h-10 w-10 sm:h-8 sm:w-8 p-0 ${currentPage === page ? "bg-brand-600 hover:bg-brand-700" : ""}`}
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-10 sm:h-8"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {!loading && projects.length === 0 && (
        <Card>
          <CardContent className="text-center py-8 sm:py-12 px-4">
            <FolderOpen className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No projects found</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              {searchTerm || filterStatus !== "all" || filterProjectType !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Get started by creating your first project"
              }
            </p>
            {canEdit && (!searchTerm && filterStatus === "all" && filterProjectType === "all") && (
              <Button
                className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto h-10"
                onClick={() => setIsDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Project
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Project Dialog */}
      <EditProjectDialog 
        isEditDialogOpen={isEditDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        editingProject={editingProject}
        setEditingProject={setEditingProject}
        handleUpdateProject={handleUpdateProject}
        updating={updateProjectMutation.isPending}
        customers={customers}
        designers={designers}
        subcontractors={subcontractors}
      />

      {/* Project Detail Dialog */}
      <ProjectDetailDialog 
        isDetailDialogOpen={isDetailDialogOpen}
        setIsDetailDialogOpen={setIsDetailDialogOpen}
        selectedProject={selectedProject}
        loadingProjectDetails={loadingProjectDetails}
        designers={designers}
        subcontractors={subcontractors}
        handleEditProject={handleEditProject}
        handleSendEmail={handleSendEmail}
        refreshSelectedProject={refreshSelectedProject}
        handleEditChangeOrder={handleEditChangeOrder}
        handleDeleteChangeOrder={handleDeleteChangeOrder}
        handleUpdatePayment={handleUpdatePayment}
        setIsPaymentDialogOpen={setIsPaymentDialogOpen}
        setEditingPayment={setEditingPayment}
        setIsChangeOrderDialogOpen={setIsChangeOrderDialogOpen}
        setNewChangeOrder={setNewChangeOrder}
        setEditingChangeOrderIndex={setEditingChangeOrderIndex}
        setIsMaterialDialogOpen={setIsMaterialDialogOpen}
        handleEditMaterial={handleEditMaterial}
        handleDeleteMaterial={handleDeleteMaterial}
        setIsEditMaterialDialogOpen={setIsEditMaterialDialogOpen}
        setIsReceiptDialogOpen={setIsReceiptDialogOpen}
        handleEditReceipt={handleEditReceipt}
        handleDeleteReceipt={handleDeleteReceipt}
        setIsEditReceiptDialogOpen={setIsEditReceiptDialogOpen}
      />
      {/* Payment Edit Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
            <DialogDescription>
              Update payment amount, due date, paid date, and status.
            </DialogDescription>
          </DialogHeader>
          {editingPayment && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="paymentName">Payment Name</Label>
                <Input
                  id="paymentName"
                  value={editingPayment.description || ""}
                  onChange={(e) => setEditingPayment({ ...editingPayment, description: e.target.value })}
                  placeholder="e.g. 1st Progress Payment"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paymentAmount">Amount</Label>
                {/* Check if this is Final Payment */}
                {editingPayment.payment_number === (selectedProject?.payment_schedule?.length || 4) ? (
                  <div className="space-y-2">
                    {editingPayment.override ? (
                      // Override enabled - amount is editable
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Override Enabled
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">$</span>
                          <Input
                            id="paymentAmount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingPayment.amount || 0}
                            onChange={(e) => setEditingPayment({...editingPayment, amount: parseFloat(e.target.value) || 0})}
                            className="flex-1"
                          />
                        </div>
                        <p className="text-xs text-amber-600 mt-1">
                          Amount is manually set and will not auto-calculate
                        </p>
                      </div>
                    ) : (
                      // Override disabled - show calculated amount and override button
                      <div>
                        <p className="text-sm font-medium text-gray-700">${editingPayment.amount?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 italic mb-3">
                          Auto-calculated: Total - (All Other Payments)
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingPayment({...editingPayment, override: true})}
                          className="text-amber-600 hover:text-amber-700 border-amber-200"
                        >
                          Enable Payment Override
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  // Not Final Payment - amount is always editable
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">$</span>
                    <Input
                      id="paymentAmount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingPayment.amount || 0}
                      onChange={(e) => setEditingPayment({...editingPayment, amount: parseFloat(e.target.value) || 0})}
                      className="flex-1"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={editingPayment.due_date}
                  onChange={(e) => setEditingPayment({...editingPayment, due_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paidDate">Paid Date</Label>
                <Input
                  id="paidDate"
                  type="date"
                  value={editingPayment.paid_date}
                  onChange={(e) => setEditingPayment({...editingPayment, paid_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={editingPayment.status} onValueChange={(value) => setEditingPayment({...editingPayment, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="paymentMethod">Payment Method</Label>
                <Select value={editingPayment.payment_method || "none"} onValueChange={(value) => setEditingPayment({...editingPayment, payment_method: value === "none" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="paymentReference">Reference / Note</Label>
                <Input
                  id="paymentReference"
                  value={editingPayment.payment_reference || ""}
                  onChange={(e) => setEditingPayment({...editingPayment, payment_reference: e.target.value})}
                  placeholder="Check #, last 4 of card, transaction #, etc."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePayment}
              disabled={updatePaymentMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updatePaymentMutation.isPending ? "Updating..." : "Update Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Order Dialog */}
      <Dialog open={isChangeOrderDialogOpen} onOpenChange={setIsChangeOrderDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingChangeOrderIndex !== null ? 'Edit Change Order' : 'Add Change Order'}</DialogTitle>
            <DialogDescription>
              {editingChangeOrderIndex !== null ? 'Update the change order details.' : 'Add a change order to modify the project scope and cost.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="changeOrderDate">Date *</Label>
              <Input
                id="changeOrderDate"
                type="date"
                value={newChangeOrder.date}
                onChange={(e) => setNewChangeOrder({...newChangeOrder, date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scopeOfWork">Scope of Work *</Label>
              <Textarea
                id="scopeOfWork"
                value={newChangeOrder.scope_of_work}
                onChange={(e) => setNewChangeOrder({...newChangeOrder, scope_of_work: e.target.value})}
                placeholder="Additional landscaping features, material upgrades, etc."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="changeOrderAmount">Amount *</Label>
              <Input
                id="changeOrderAmount"
                type="number"
                value={newChangeOrder.amount}
                onChange={(e) => setNewChangeOrder({...newChangeOrder, amount: e.target.value})}
                placeholder="5000"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsChangeOrderDialogOpen(false);
              setEditingChangeOrderIndex(null);
              setNewChangeOrder({ date: "", scope_of_work: "", amount: "" });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddOrUpdateChangeOrder}
              disabled={addChangeOrderMutation.isPending || !newChangeOrder.date || !newChangeOrder.scope_of_work || !newChangeOrder.amount}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {addChangeOrderMutation.isPending ? (editingChangeOrderIndex !== null ? "Updating..." : "Adding...") : (editingChangeOrderIndex !== null ? "Update Change Order" : "Add Change Order")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={isMaterialDialogOpen} onOpenChange={setIsMaterialDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Material</DialogTitle>
            <DialogDescription>
              Add a material to the project materials list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="materialName">Material Name *</Label>
              <Input
                id="materialName"
                value={newMaterial.name}
                onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                placeholder="Concrete blocks, plants, etc."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="materialCost">Cost *</Label>
                <Input
                  id="materialCost"
                  type="number"
                  value={newMaterial.cost}
                  onChange={(e) => setNewMaterial({...newMaterial, cost: e.target.value})}
                  placeholder="1500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialVendor">Vendor/Subcontractor</Label>
              <Select
                value={newMaterial.vendor_id}
                onValueChange={(value) => {
                  const vendor = vendors.find(v => v.id === value);
                  const subcontractor = subcontractors.find(s => s.id === value);
                  const selected = vendor || subcontractor;
                  setNewMaterial({
                    ...newMaterial,
                    vendor_id: value,
                    company_name: selected ? (selected.company_name || selected.name) : newMaterial.company_name
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor/subcontractor or type below" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.length > 0 && (
                    <>
                      <SelectItem value="vendors-header" disabled className="font-semibold">
                        Vendors
                      </SelectItem>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.company_name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {subcontractors.length > 0 && (
                    <>
                      <SelectItem value="subcontractors-header" disabled className="font-semibold">
                        Subcontractors
                      </SelectItem>
                      {subcontractors.map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>
                          {sub.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Not in the list?</Label>
              <Button type="button" variant="outline" className="w-full justify-start" onClick={() => openQuickAdd("newMaterial")}>
                <Plus className="h-4 w-4 mr-2" /> Add a new vendor / subcontractor
              </Button>
              {newMaterial.company_name && !newMaterial.vendor_id && (
                <p className="text-xs text-gray-500">Current: {newMaterial.company_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialDate">Date</Label>
              <Input
                id="materialDate"
                type="date"
                value={newMaterial.date}
                onChange={(e) => setNewMaterial({...newMaterial, date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialInvoice">Invoice #</Label>
              <Input
                id="materialInvoice"
                value={newMaterial.invoice_number}
                onChange={(e) => setNewMaterial({...newMaterial, invoice_number: e.target.value})}
                placeholder="Invoice number or reference"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="materialDescription">Description</Label>
              <Textarea
                id="materialDescription"
                value={newMaterial.description}
                onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                placeholder="Additional notes or description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMaterialDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddMaterial}
              disabled={addMaterialMutation.isPending || !newMaterial.name || !newMaterial.cost}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {addMaterialMutation.isPending ? "Adding..." : "Add Material"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={isReceiptDialogOpen} onOpenChange={setIsReceiptDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Receipt</DialogTitle>
            <DialogDescription>
              Add a receipt to track project costs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="receiptItem">Item *</Label>
              <Input
                id="receiptItem"
                value={newReceipt.item}
                onChange={(e) => setNewReceipt({...newReceipt, item: e.target.value})}
                placeholder="Tools, equipment, supplies, etc."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiptCost">Cost *</Label>
              <Input
                id="receiptCost"
                type="number"
                value={newReceipt.cost}
                onChange={(e) => setNewReceipt({...newReceipt, cost: e.target.value})}
                placeholder="150.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiptVendor">Vendor/Subcontractor/Designer</Label>
              <Select
                value={newReceipt.vendor_id}
                onValueChange={(value) => {
                  const vendor = vendors.find(v => v.id === value);
                  const subcontractor = subcontractors.find(s => s.id === value);
                  const designer = designers.find(d => d.id === value);
                  const selected = vendor || subcontractor || designer;
                  setNewReceipt({
                    ...newReceipt,
                    vendor_id: value,
                    company_name: selected ? (selected.company_name || selected.name) : newReceipt.company_name
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor/subcontractor/designer or type below" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.length > 0 && (
                    <>
                      <SelectItem value="vendors-header" disabled className="font-semibold">
                        Vendors
                      </SelectItem>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.company_name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {subcontractors.length > 0 && (
                    <>
                      <SelectItem value="subcontractors-header" disabled className="font-semibold">
                        Subcontractors
                      </SelectItem>
                      {subcontractors.map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>
                          {sub.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {designers.length > 0 && (
                    <>
                      <SelectItem value="designers-header" disabled className="font-semibold">
                        Designers
                      </SelectItem>
                      {designers.map(designer => (
                        <SelectItem key={designer.id} value={designer.id}>
                          {designer.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Not in the list?</Label>
              <Button type="button" variant="outline" className="w-full justify-start" onClick={() => openQuickAdd("newReceipt")}>
                <Plus className="h-4 w-4 mr-2" /> Add a new vendor / subcontractor
              </Button>
              {newReceipt.company_name && !newReceipt.vendor_id && (
                <p className="text-xs text-gray-500">Current: {newReceipt.company_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiptDate">Date</Label>
              <Input
                id="receiptDate"
                type="date"
                value={newReceipt.date}
                onChange={(e) => setNewReceipt({...newReceipt, date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiptInvoice">Invoice #</Label>
              <Input
                id="receiptInvoice"
                value={newReceipt.invoice_number}
                onChange={(e) => setNewReceipt({...newReceipt, invoice_number: e.target.value})}
                placeholder="Invoice number or reference"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiptDescription">Description</Label>
              <Textarea
                id="receiptDescription"
                value={newReceipt.description}
                onChange={(e) => setNewReceipt({...newReceipt, description: e.target.value})}
                placeholder="Additional details about this purchase..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReceiptDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddReceipt}
              disabled={addReceiptMutation.isPending || !newReceipt.item || !newReceipt.cost}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {addReceiptMutation.isPending ? "Adding..." : "Add Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Send Email Dialog */}
      <Dialog open={isSendEmailDialogOpen} onOpenChange={setIsSendEmailDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[700px] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              Send an email using a template to {emailData.to_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-2">
              <Label htmlFor="emailTemplate">Email Template *</Label>
              <Select value={emailData.template_id} onValueChange={handleTemplateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select email template" />
                </SelectTrigger>
                <SelectContent>
                  {(templatesData?.data?.templates || []).map((template) => (
                    <SelectItem key={template.id || template._id} value={template.id || template._id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="toEmail">To Email *</Label>
                <Input
                  id="toEmail"
                  type="email"
                  value={emailData.to_email}
                  onChange={(e) => setEmailData({...emailData, to_email: e.target.value})}
                  placeholder="customer@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="toName">To Name *</Label>
                <Input
                  id="toName"
                  value={emailData.to_name}
                  onChange={(e) => setEmailData({...emailData, to_name: e.target.value})}
                  placeholder="Customer Name"
                />
              </div>
            </div>

            {selectedEmailTemplate && selectedEmailTemplate.variables && selectedEmailTemplate.variables.length > 0 && (
              <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
                <Label className="text-xs font-medium">Template Variables:</Label>
                <div className="flex flex-wrap gap-1">
                  {selectedEmailTemplate.variables.map((variable, index) => (
                    <Badge key={index} variant="secondary" className="text-xs py-0 px-1">
                      {"{" + variable + "}"}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Auto-filled with customer and project data
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="emailSubject">Subject *</Label>
              <Input
                id="emailSubject"
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                placeholder="Enter email subject"
              />
              {/* Show preview of subject with variables replaced */}
              {emailData.subject && getUnresolvedVariables(emailData.subject).length > 0 && (
                <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
                  <span className="font-medium">Preview: </span>
                  {getPreviewContent(emailData.subject)}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailBody">Email Body *</Label>
              <Textarea
                id="emailBody"
                value={emailData.body}
                onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                placeholder="Enter email body"
                rows={6}
                className="resize-none"
              />
            </div>

            {/* Preview section for all HTML emails */}
            {emailData.body && (
              <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <Label className="text-blue-800 font-medium flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Email Preview
                </Label>
                <div className="text-sm bg-white p-3 rounded border max-h-48 overflow-y-auto overflow-x-hidden">
                  <div className="font-medium text-gray-700 mb-2 truncate">Subject: {getPreviewContent(emailData.subject)}</div>
                  <div 
                    className="text-gray-600 prose prose-sm max-w-full break-words [&_*]:max-w-full [&_img]:max-w-full [&_table]:max-w-full [&_table]:table-fixed"
                    dangerouslySetInnerHTML={{ __html: getPreviewContent(emailData.body) }}
                  />
                </div>
                {getUnresolvedVariables(getPreviewContent(emailData.subject + emailData.body)).length > 0 && (
                  <div className="text-amber-700 text-xs flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Some variables may not have data: {getUnresolvedVariables(getPreviewContent(emailData.subject + emailData.body)).join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSendEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmailSubmit}
              disabled={sendEmailMutation.isPending || !emailData.subject || !emailData.body || !emailData.to_email || !emailData.to_name}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CompanyCam Link Dialog */}
      <Dialog open={isCompanyCamLinkDialogOpen} onOpenChange={setIsCompanyCamLinkDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Camera className="mr-2 h-5 w-5 text-blue-600" />
              Link to CompanyCam
            </DialogTitle>
            <DialogDescription>
              Connect your project to CompanyCam for photo documentation and project tracking
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Link Type Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium">How would you like to connect to CompanyCam?</Label>
              <div className="grid grid-cols-1 gap-3">
                <div 
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    linkingType === "create" ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setLinkingType("create")}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                      linkingType === "create" ? "border-blue-500 bg-blue-500" : "border-gray-300"
                    }`}>
                      {linkingType === "create" && <div className="w-full h-full rounded-full bg-white transform scale-50"></div>}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Create New CompanyCam Project</div>
                      <div className="text-sm text-gray-600">
                        Create a new project in CompanyCam with details from this LandscapeFlow project
                      </div>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    linkingType === "link" ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setLinkingType("link")}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                      linkingType === "link" ? "border-blue-500 bg-blue-500" : "border-gray-300"
                    }`}>
                      {linkingType === "link" && <div className="w-full h-full rounded-full bg-white transform scale-50"></div>}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Link to Existing CompanyCam Project</div>
                      <div className="text-sm text-gray-600">
                        Search and connect to an existing project in your CompanyCam account
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Create New Project Preview */}
            {linkingType === "create" && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">New Project Details</Label>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs text-gray-600">Project Name</Label>
                    <p className="text-gray-900 font-medium">{selectedProject?.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Customer</Label>
                    <p className="text-gray-900">{selectedProject?.customer?.name || 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-gray-600">Address</Label>
                    <p className="text-gray-900">{selectedProject?.jobsite_address || selectedProject?.address || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Search Existing Projects */}
            {linkingType === "link" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Existing CompanyCam Project</Label>
                  
                  {/* Simple inline combobox - no Popover */}
                  <div className="relative">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search by name, address, or customer..."
                        value={companyCamSearchQuery}
                        onChange={(e) => setCompanyCamSearchQuery(e.target.value)}
                        onFocus={() => setComboboxOpen(true)}
                        className="pl-9 pr-10"
                        disabled={searchingCompanyCam}
                      />
                      {companyCamSearchQuery && (
                        <button
                          onClick={() => setCompanyCamSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    
                    {/* Dropdown List - shows when input is focused or has value */}
                    {comboboxOpen && (
                      <>
                        {/* Backdrop to close dropdown when clicking outside */}
                        <div 
                          className="fixed inset-0 z-40"
                          onClick={() => setComboboxOpen(false)}
                        />
                        
                        {/* Dropdown content */}
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-50 max-h-[400px] overflow-y-auto">
                          {searchingCompanyCam ? (
                            <div className="p-4 text-center text-sm text-gray-500">
                              Loading projects...
                            </div>
                          ) : (
                            <>
                              {(() => {
                                // Client-side filtering of loaded projects
                                const filteredProjects = allCompanyCamProjects.filter((project) => {
                                  if (!companyCamSearchQuery) return true;
                                  
                                  const query = companyCamSearchQuery.toLowerCase();
                                  const addressStr = project.address 
                                    ? (typeof project.address === 'string' 
                                        ? project.address 
                                        : `${project.address.street || ''} ${project.address.city || ''} ${project.address.state || ''} ${project.address.zip || ''}`)
                                    : '';
                                  
                                  const searchText = `${project.name} ${addressStr} ${project.customer_name || ''}`.toLowerCase();
                                  return searchText.includes(query);
                                });
                                
                                return (
                                  <>
                                    {filteredProjects.length > 0 ? (
                                      <>
                                        {filteredProjects.map((project) => {
                                          const addressStr = project.address 
                                            ? (typeof project.address === 'string' 
                                                ? project.address 
                                                : `${project.address.street || ''}, ${project.address.city || ''}, ${project.address.state || ''} ${project.address.zip || ''}`.replace(/,\s*,/g, ',').trim())
                                            : '';
                                          
                                          const isSelected = selectedCompanyCamProject?.id === project.id;
                                          
                                          return (
                                            <div
                                              key={project.id}
                                              onClick={() => {
                                                setSelectedCompanyCamProject(project);
                                                setComboboxOpen(false);
                                                setCompanyCamSearchQuery(project.name);
                                              }}
                                              className={cn(
                                                "flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100",
                                                isSelected && "bg-gray-50"
                                              )}
                                            >
                                              <Check
                                                className={cn(
                                                  "h-4 w-4 mt-0.5 flex-shrink-0",
                                                  isSelected ? "opacity-100 text-brand-600" : "opacity-0"
                                                )}
                                              />
                                              <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm truncate">{project.name}</div>
                                                {addressStr && (
                                                  <div className="text-xs text-gray-500 truncate">{addressStr}</div>
                                                )}
                                                {project.customer_name && (
                                                  <div className="text-xs text-gray-400 truncate">Customer: {project.customer_name}</div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                        
                                        {/* Load More Button - only show if there are more pages to load */}
                                        {companyCamHasMore && (
                                          <div className="border-t p-2">
                                            <Button
                                              variant="ghost"
                                              className="w-full text-sm text-brand-600 hover:text-emerald-700 hover:bg-emerald-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleLoadMoreCompanyCamProjects();
                                              }}
                                              disabled={searchingCompanyCam}
                                            >
                                              {searchingCompanyCam 
                                                ? "Loading..." 
                                                : `Load More`}
                                            </Button>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="p-4 text-center text-sm text-gray-500">
                                        {companyCamSearchQuery 
                                          ? `No projects found matching "${companyCamSearchQuery}"`
                                          : "No projects found"}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Status text */}
                  <p className="text-xs text-gray-500">
                    {searchingCompanyCam
                      ? "Loading projects..."
                      : selectedCompanyCamProject 
                        ? `Selected: ${selectedCompanyCamProject.name}`
                        : `${allCompanyCamProjects.length} project${allCompanyCamProjects.length !== 1 ? 's' : ''} available`}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsCompanyCamLinkDialogOpen(false);
                setSelectedCompanyCamProject(null);
                setCompanyCamSearchResults([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleLinkToCompanyCam(linkingType)}
              disabled={
                linkCompanyCamMutation.isPending || 
                (linkingType === "link" && !selectedCompanyCamProject)
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {linkCompanyCamMutation.isPending ? "Linking..." : 
                linkingType === "create" ? "Create & Link Project" : "Link Selected Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* CompanyCam Unlink Confirmation Dialog */}
      <Dialog open={isCompanyCamUnlinkDialogOpen} onOpenChange={setIsCompanyCamUnlinkDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Unlink CompanyCam Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to unlink this CompanyCam project? This will not delete the project in CompanyCam, only remove the link.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCompanyCamUnlinkDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmUnlinkCompanyCam}
              disabled={unlinkCompanyCamMutation.isPending}
            >
              {unlinkCompanyCamMutation.isPending ? "Unlinking..." : "Unlink"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit Material Dialog */}
      <Dialog open={isEditMaterialDialogOpen} onOpenChange={setIsEditMaterialDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Material</DialogTitle>
            <DialogDescription>
              Update material information.
            </DialogDescription>
          </DialogHeader>
          {editMaterial && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editMaterialName">Material Name *</Label>
                <Input
                  id="editMaterialName"
                  value={editMaterial.name}
                  onChange={(e) => setEditMaterial({...editMaterial, name: e.target.value})}
                  placeholder="Concrete blocks, plants, etc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editMaterialCost">Cost *</Label>
                <Input
                  id="editMaterialCost"
                  type="number"
                  value={editMaterial.cost}
                  onChange={(e) => setEditMaterial({...editMaterial, cost: e.target.value})}
                  placeholder="1500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editMaterialVendor">Vendor/Subcontractor</Label>
                <Select
                  value={editMaterial.vendor_id || ""}
                  onValueChange={(value) => {
                    const vendor = vendors.find(v => v.id === value);
                    const subcontractor = subcontractors.find(s => s.id === value);
                    const selected = vendor || subcontractor;
                    setEditMaterial({
                      ...editMaterial,
                      vendor_id: value,
                      company_name: selected ? (selected.company_name || selected.name) : editMaterial.company_name
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor/subcontractor or type below" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.length > 0 && (
                      <>
                        <SelectItem value="vendors-header" disabled className="font-semibold">
                          Vendors
                        </SelectItem>
                        {vendors.map(vendor => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.company_name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {subcontractors.length > 0 && (
                      <>
                        <SelectItem value="subcontractors-header" disabled className="font-semibold">
                          Subcontractors
                        </SelectItem>
                        {subcontractors.map(sub => (
                          <SelectItem key={sub.id} value={sub.id}>
                            {sub.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Not in the list?</Label>
                <Button type="button" variant="outline" className="w-full justify-start" onClick={() => openQuickAdd("editMaterial")}>
                  <Plus className="h-4 w-4 mr-2" /> Add a new vendor / subcontractor
                </Button>
                {editMaterial.company_name && !editMaterial.vendor_id && (
                  <p className="text-xs text-gray-500">Current: {editMaterial.company_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="editMaterialDate">Date</Label>
                <Input
                  id="editMaterialDate"
                  type="date"
                  value={editMaterial.date || ""}
                  onChange={(e) => setEditMaterial({...editMaterial, date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editMaterialInvoice">Invoice #</Label>
                <Input
                  id="editMaterialInvoice"
                  value={editMaterial.invoice_number || ""}
                  onChange={(e) => setEditMaterial({...editMaterial, invoice_number: e.target.value})}
                  placeholder="Invoice number or reference"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editMaterialDescription">Description</Label>
                <Textarea
                  id="editMaterialDescription"
                  value={editMaterial.description || ""}
                  onChange={(e) => setEditMaterial({...editMaterial, description: e.target.value})}
                  placeholder="Additional notes or description"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditMaterialDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateMaterial}
              disabled={updateMaterialMutation.isPending || !editMaterial?.name || !editMaterial?.cost}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateMaterialMutation.isPending ? "Updating..." : "Update Material"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Receipt Dialog */}
      <Dialog open={isEditReceiptDialogOpen} onOpenChange={setIsEditReceiptDialogOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Receipt</DialogTitle>
            <DialogDescription>
              Update receipt information.
            </DialogDescription>
          </DialogHeader>
          {editReceipt && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editReceiptItem">Item *</Label>
                <Input
                  id="editReceiptItem"
                  value={editReceipt.item}
                  onChange={(e) => setEditReceipt({...editReceipt, item: e.target.value})}
                  placeholder="Tools, equipment, supplies, etc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editReceiptCost">Cost *</Label>
                <Input
                  id="editReceiptCost"
                  type="number"
                  value={editReceipt.cost}
                  onChange={(e) => setEditReceipt({...editReceipt, cost: e.target.value})}
                  placeholder="150.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editReceiptVendor">Vendor/Subcontractor/Designer</Label>
                <Select
                  value={editReceipt.vendor_id || ""}
                  onValueChange={(value) => {
                    const vendor = vendors.find(v => v.id === value);
                    const subcontractor = subcontractors.find(s => s.id === value);
                    const designer = designers.find(d => d.id === value);
                    const selected = vendor || subcontractor || designer;
                    setEditReceipt({
                      ...editReceipt,
                      vendor_id: value,
                      company_name: selected ? (selected.company_name || selected.name) : editReceipt.company_name
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor/subcontractor/designer or type below" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.length > 0 && (
                      <>
                        <SelectItem value="vendors-header" disabled className="font-semibold">
                          Vendors
                        </SelectItem>
                        {vendors.map(vendor => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.company_name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {subcontractors.length > 0 && (
                      <>
                        <SelectItem value="subcontractors-header" disabled className="font-semibold">
                          Subcontractors
                        </SelectItem>
                        {subcontractors.map(sub => (
                          <SelectItem key={sub.id} value={sub.id}>
                            {sub.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {designers.length > 0 && (
                      <>
                        <SelectItem value="designers-header" disabled className="font-semibold">
                          Designers
                        </SelectItem>
                        {designers.map(designer => (
                          <SelectItem key={designer.id} value={designer.id}>
                            {designer.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Not in the list?</Label>
                <Button type="button" variant="outline" className="w-full justify-start" onClick={() => openQuickAdd("editReceipt")}>
                  <Plus className="h-4 w-4 mr-2" /> Add a new vendor / subcontractor
                </Button>
                {editReceipt.company_name && !editReceipt.vendor_id && (
                  <p className="text-xs text-gray-500">Current: {editReceipt.company_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="editReceiptDate">Date</Label>
                <Input
                  id="editReceiptDate"
                  type="date"
                  value={editReceipt.date || ""}
                  onChange={(e) => setEditReceipt({...editReceipt, date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editReceiptInvoice">Invoice #</Label>
                <Input
                  id="editReceiptInvoice"
                  value={editReceipt.invoice_number || ""}
                  onChange={(e) => setEditReceipt({...editReceipt, invoice_number: e.target.value})}
                  placeholder="Invoice number or reference"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editReceiptDescription">Description</Label>
                <Textarea
                  id="editReceiptDescription"
                  value={editReceipt.description || ""}
                  onChange={(e) => setEditReceipt({...editReceipt, description: e.target.value})}
                  placeholder="Additional details about this purchase..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditReceiptDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateReceipt}
              disabled={updateReceiptMutation.isPending || !editReceipt?.item || !editReceipt?.cost}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateReceiptMutation.isPending ? "Updating..." : "Update Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Change Order Confirmation Dialog */}
      <Dialog open={isDeleteChangeOrderDialogOpen} onOpenChange={setIsDeleteChangeOrderDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Change Order</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this change order? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteChangeOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteChangeOrder}
              disabled={updateProjectMutation.isPending}
            >
              {updateProjectMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Material Confirmation Dialog */}
      <Dialog open={isDeleteMaterialDialogOpen} onOpenChange={setIsDeleteMaterialDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Material</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this material? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteMaterialDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteMaterial}
              disabled={deleteMaterialMutation.isPending}
            >
              {deleteMaterialMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Receipt Confirmation Dialog */}
      <Dialog open={isDeleteReceiptDialogOpen} onOpenChange={setIsDeleteReceiptDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Receipt</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this receipt? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteReceiptDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteReceipt}
              disabled={deleteReceiptMutation.isPending}
            >
              {deleteReceiptMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={isDeleteProjectDialogOpen} onOpenChange={setIsDeleteProjectDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <span className="font-semibold">{projectToDelete?.name}</span>? This action cannot be undone and will permanently remove all project data, including:
            </p>
            <ul className="mt-3 ml-6 text-sm text-gray-600 list-disc space-y-1">
              <li>Project timeline and status</li>
              <li>Financial records and payments</li>
              <li>Materials and receipts</li>
              <li>Change orders</li>
              <li>All associated documents</li>
            </ul>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsDeleteProjectDialogOpen(false);
                setProjectToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteProject}
            >
              Delete Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={isStatusChangeDialogOpen} onOpenChange={setIsStatusChangeDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Change Project Status</DialogTitle>
            <DialogDescription>
              Update the status for <span className="font-semibold">{statusChangeData.projectName}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <div className="p-2 bg-gray-100 rounded">
                <Badge className={`${getStatusColor(statusChangeData.newStatus)} text-white`}>
                  {getStatusLabel(statusChangeData.newStatus)}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="statusDate">Status Change Date</Label>
              <Input
                id="statusDate"
                type="date"
                value={statusChangeData.statusDate}
                onChange={(e) => setStatusChangeData({...statusChangeData, statusDate: e.target.value})}
              />
              <p className="text-xs text-gray-500">
                Change this if the status actually changed on a different date
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsStatusChangeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmStatusChange}
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Vendor / Subcontractor */}
      <QuickAddVendorDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        onCreated={handleQuickAddCreated}
      />

    </div>
  );
}
